import {
  Box,
  Card,
  CardContent,
  Chip,
  Fade,
  FormHelperText,
  Grid,
  makeStyles,
  Tab,
  Tabs,
  TextField,
  Typography
} from "@material-ui/core";
import CardTitle from "@saleor/components/CardTitle/CardTitle";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { ChangeEvent, FormChange } from "@saleor/hooks/useForm";
import { maybe } from "@saleor/misc";
import {
  CouponOccurenceEnum,
  CouponOccurenceExcludeEnum
} from "@saleor/types/globalTypes";
import classNames from "classnames";
import moment from "moment-timezone";
import React, { createContext, useContext, useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { TooltipArrowLeft } from "../CouponDetails/Tooltip";

interface CouponSchedulingProps {
  disabled: boolean;
  data: any;
  onChange: (event: ChangeEvent<any>) => void;
  localError: {
    couponStartDate: string;
    couponStartTime: string;
    couponEndDate: string;
    couponEndTime: string;
    occurrence: string;
    redeemingStartDate: string;
    redeemingStartTime: string;
    redeemingEndDate: string;
    redeemingEndTime: string;
    excludedDays: string[];
    eventStartDate?: string;
    eventStartTime?: string;
  };
}

enum CouponsTab {
  Coupon = "coupon",
  Redeeming = "redeeming",
  Event = "event"
}

const redeemingOptions = [
  {
    label: "Daily",
    value: CouponOccurenceEnum.DAILY
  },
  {
    label: "Weekly",
    value: CouponOccurenceEnum.WEEKLY
  },
  {
    label: "Monthly",
    value: CouponOccurenceEnum.MONTHLY
  },
  {
    label: "Yearly",
    value: CouponOccurenceEnum.YEARLY
  }
];

const dayOptions = [
  { label: "SUN", value: CouponOccurenceExcludeEnum.SUNDAY },
  { label: "MON", value: CouponOccurenceExcludeEnum.MONDAY },
  { label: "TUE", value: CouponOccurenceExcludeEnum.TUESDAY },
  { label: "WED", value: CouponOccurenceExcludeEnum.WEDNESDAY },
  { label: "THU", value: CouponOccurenceExcludeEnum.THURSDAY },
  { label: "FRI", value: CouponOccurenceExcludeEnum.FRIDAY },
  { label: "SAT", value: CouponOccurenceExcludeEnum.SATURDAY }
];

const tabColumns = {
  coupon: [
    "couponStartDate",
    "couponEndDate",
    "couponStartTime",
    "couponEndTime",
    "excludedDays",
    "occurrence"
  ],
  event: ["eventStartDate", "eventStartTime"],
  redeeming: [
    "redeemingStartDate",
    "redeemingEndDate",
    "redeemingStartTime",
    "redeemingEndTime"
  ]
};

const useStyles = makeStyles(
  {
    activeChipItem: {
      backgroundColor: "#0030FF !important",
      color: "#fff !important"
    },
    chipItem: {
      backgroundColor: "#fff",
      border: `1px solid #0030FF`,
      borderRadius: "200rem",
      color: "#0030FF",
      cursor: "pointer"
    },
    chips: {
      display: "flex",
      gap: "3px"
    },
    couponTabItem: {
      fontSize: 16,
      padding: "6px 24px",
      textTransform: "none"
    },
    couponTabs: {
      "& .MuiTabs-flexContainer": {
        borderBottom: "2px solid #CED3DD"
      },
      "& .MuiTabs-indicator": {
        backgroundColor: "#0030FF"
      },
      marginBottom: 12
    },
    datePicker: {
      marginBottom: 20
    },
    errorCouponTab: {
      color: "#EC5E77 !important"
    },
    helperText: {
      color: "#EC5D77",
      margin: "3px 0px"
    },
    redeemingSelection: {
      marginBottom: 12
    },
    schedulingContent: {
      padding: 0
    },
    schedulingContentItem: {
      padding: "10px 24px"
    },
    schedulingWrapper: {
      marginTop: 16
    },
    selectedCouponTabItem: {
      color: "#0030FF"
    },
    tabRoot: {
      fontSize: "16px",
      textTransform: "none"
    }
  },
  {
    name: "CouponScheduling"
  }
);

const CouponSchedulingContext = createContext({
  activeTab: undefined,
  data: undefined,
  disabled: undefined,
  localError: undefined,
  onChange: undefined
});

const useCouponScheduling = () => useContext(CouponSchedulingContext);

const CouponScheduling: React.FC<CouponSchedulingProps> = props => {
  const { data, onChange, localError, disabled } = props;
  const classes = useStyles(props);
  const [activeTab, setActiveTab] = useState(CouponsTab.Coupon);

  const resetField = fieldName => {
    onChange({
      target: {
        name: fieldName,
        value: null
      }
    });
  };

  useEffect(() => {
    if (data.couponClassification === "DEALS") {
      setActiveTab(CouponsTab.Coupon);
    }
  }, [data.couponClassification]);

  return (
    <CouponSchedulingContext.Provider
      value={{
        activeTab,
        data,
        disabled,
        localError,
        onChange
      }}
    >
      <Card className={classes.schedulingWrapper}>
        <CardHeader title="Scheduling" />
        <CardContent className={classes.schedulingContent}>
          <CardTabs setActiveTab={setActiveTab} resetField={resetField} />
          <CouponContent />
        </CardContent>
      </Card>
    </CouponSchedulingContext.Provider>
  );
};

const CardHeader = ({ title }) => (
  <CardTitle title={<strong>{title}</strong>}></CardTitle>
);

const CardTabs = ({ setActiveTab, resetField }) => {
  const intl = useIntl();
  const classes = useStyles({});
  const { activeTab, data, localError } = useCouponScheduling();
  const { couponClassification } = data;

  const tabItems = [
    {
      id: "claimingScheduleTab",
      label: intl.formatMessage({
        defaultMessage: "Claiming Schedule"
      }),
      tooltip:
        "Should indicate preferred live date of the coupon in the app. It can also serve as an early announcement.",
      value: CouponsTab.Coupon
    },
    {
      id: "redeemingScheduleTab",
      label: intl.formatMessage({
        defaultMessage: "Redeeming Schedule"
      }),
      tooltip:
        "Should indicate the actual duration when coupon can be redeemed by customer.",
      value: CouponsTab.Redeeming
    },
    couponClassification === "EVENTS" && {
      id: "eventScheduleTab",
      label: intl.formatMessage({
        defaultMessage: "Event Schedule"
      }),
      tooltip:
        couponClassification === "EVENTS"
          ? "Should indicate the actual schedule of the event."
          : null,
      value: CouponsTab.Event
    }
  ].filter(Boolean);

  const isCouponScheduled =
    data.couponStartDate &&
    data.couponStartTime &&
    data.couponEndDate &&
    data.couponEndTime;

  const handleTabChange = (event, newValue) => {
    if (
      newValue === CouponsTab.Redeeming &&
      (data.redeemingStartDate || data.redeemingEndDate)
    ) {
      redeemingDateRangeCleanup();
    }
    setActiveTab(newValue);
    event.preventDefault();
  };

  const redeemingDateRangeCleanup = () => {
    const {
      isValidRedeemStart,
      isValidRedeemEnd
    } = checkIfValidRedeemingDates();

    if (data.redeemingStartDate && !isValidRedeemStart) {
      resetField("redeemingStartDate");
    }

    if (data.redeemingEndDate && !isValidRedeemEnd) {
      resetField("redeemingEndDate");
    }
  };

  const checkIfValidRedeemingDates = () => {
    const couponStartDateMoment = moment(data.couponStartDate);
    const redeemingStartDateMoment = data.redeemingStartDate
      ? moment(data.redeemingStartDate)
      : null;
    const redeemingEndDateMoment = data.redeemingEndDate
      ? moment(data.redeemingEndDate)
      : null;

    return {
      isValidRedeemEnd:
        !!redeemingEndDateMoment &&
        !redeemingEndDateMoment.isBefore(couponStartDateMoment),
      isValidRedeemStart:
        !!redeemingStartDateMoment &&
        !redeemingStartDateMoment.isBefore(couponStartDateMoment)
    };
  };

  const tabWithErrors = identifyTabWithErrors(localError);

  return (
    <Tabs
      value={activeTab}
      className={classes.couponTabs}
      onChange={handleTabChange}
    >
      {tabItems.map(tabItem => {
        let isDisabled = false;
        if (tabItem.value === CouponsTab.Redeeming && !isCouponScheduled) {
          isDisabled = true;
        } else if (tabItem.value === CouponsTab.Event) {
          isDisabled = !data.redeemingStartDate || !data.redeemingEndDate;
        }

        return (
          <Tab
            id={tabItem.id}
            className={classNames(classes.couponTabItem, {
              [classes.errorCouponTab]: !!tabWithErrors[tabItem.value]
            })}
            classes={{ selected: classes.selectedCouponTabItem }}
            disabled={isDisabled}
            label={
              <TooltipArrowLeft title={tabItem.tooltip || ""} placement="right">
                <span>{tabItem.label}</span>
              </TooltipArrowLeft>
            }
            value={tabItem.value}
            onClick={() => setActiveTab(tabItem.value)}
            key={tabItem.value}
          />
        );
      })}
    </Tabs>
  );
};

const identifyTabWithErrors = localError => {
  const tabWithErrors = {};
  Object.keys(tabColumns).forEach(tabItem => {
    tabColumns[tabItem].forEach(column => {
      if (!!localError[column]) {
        tabWithErrors[tabItem] = true;
      }
    });
  });

  return tabWithErrors;
};

const CouponContent = () => {
  const classes = useStyles({});
  const { activeTab } = useCouponScheduling();

  return (
    <Grid container spacing={3} className={classes.schedulingContentItem}>
      {activeTab !== CouponsTab.Event && (
        <Grid item xs={6}>
          <CouponTab />
          <RedeemingTab />
        </Grid>
      )}
      {activeTab !== CouponsTab.Event && (
        <Grid item xs={6}>
          <OccurrenceSelection />
          <ExcludeDays />
        </Grid>
      )}
      {activeTab === CouponsTab.Event && (
        <Grid item xs={12}>
          <EventTab />
        </Grid>
      )}
    </Grid>
  );
};

const CouponTab = () => {
  const { activeTab, data, onChange, disabled } = useCouponScheduling();

  const {
    couponStartDate: startDate,
    couponStartTime: startTime,
    couponEndDate: endDate,
    couponEndTime: endTime
  } = data;

  const [isDataVisible, setDataIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDataIsVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fade in={activeTab === CouponsTab.Coupon && isDataVisible}>
      <Box>
        {activeTab === CouponsTab.Coupon && isDataVisible ? (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <DatePicker
                id="couponStartDate"
                label="Start Date"
                value={startDate}
                onChange={onChange}
                inputProps={{
                  max: data.couponEndDate,
                  min: new Date().toISOString().split("T")[0]
                }}
                disabled={disabled}
              />
              <TimePicker
                id="couponStartTime"
                label="Start Time"
                value={startTime}
                onChange={onChange}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                id="couponEndDate"
                label="End Date"
                value={endDate}
                onChange={onChange}
                inputProps={{
                  min: data.couponStartDate
                }}
                disabled={disabled}
              />
              <TimePicker
                id="couponEndTime"
                label="End Time"
                value={endTime}
                onChange={onChange}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </Fade>
  );
};

const RedeemingTab = () => {
  const { activeTab, data, onChange, disabled } = useCouponScheduling();

  const {
    redeemingStartDate: startDate,
    redeemingStartTime: startTime,
    redeemingEndDate: endDate,
    redeemingEndTime: endTime
  } = data;

  const [isDataVisible, setDataIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDataIsVisible(true);
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Fade in={activeTab === CouponsTab.Redeeming && isDataVisible}>
      <Box>
        {activeTab === CouponsTab.Redeeming && isDataVisible ? (
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <DatePicker
                id="redeemingStartDate"
                label="Start Date"
                value={startDate}
                onChange={onChange}
                inputProps={{
                  min: data.couponStartDate
                }}
                disabled={disabled}
              />
              <TimePicker
                id="redeemingStartTime"
                label="Start Time"
                value={startTime}
                onChange={onChange}
                disabled={disabled}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                id="redeemingEndDate"
                label="End Date"
                value={endDate}
                onChange={onChange}
                inputProps={{
                  min: !!data.redeemingStartDate
                    ? data.redeemingStartDate
                    : data.couponStartDate
                }}
                disabled={disabled}
              />
              <TimePicker
                id="redeemingEndTime"
                label="End Time"
                value={endTime}
                onChange={onChange}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </Fade>
  );
};

const EventTab = () => {
  const { activeTab, data, onChange, disabled } = useCouponScheduling();

  const [isDataVisible, setDataIsVisible] = useState(false);

  const {
    eventStartDate: startDate,
    eventStartTime: startTime,
    couponClassification
  } = data;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDataIsVisible(true);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const isEventTabVisible = couponClassification === "EVENTS";

  return (
    <Fade in={activeTab === CouponsTab.Event && isDataVisible}>
      <Box>
        {activeTab === CouponsTab.Event &&
        isDataVisible &&
        isEventTabVisible ? (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DatePicker
                id="eventStartDate"
                label="Start Date"
                value={startDate}
                onChange={onChange}
                inputProps={{
                  min: data.redeemingStartDate
                }}
                disabled={disabled}
              />
              <TimePicker
                id="eventStartTime"
                label="Start Time"
                value={startTime}
                onChange={onChange}
                disabled={disabled}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </Fade>
  );
};

interface DateTimeRangeProps {
  min?: string;
  max?: string;
}

interface DateTimePickerProps {
  disabled: boolean;
  id: string;
  label: string;
  value: string;
  onChange: FormChange;
  inputProps?: DateTimeRangeProps;
}

const DatePicker = ({
  id,
  label,
  value,
  inputProps,
  disabled
}: DateTimePickerProps) => {
  const { localError, onChange } = useCouponScheduling();
  const classes = useStyles({});
  const handleChange = e => {
    onChange({
      target: {
        name: e.target.name,
        value: e.target.value
      }
    });
  };

  return (
    <TextField
      id={id}
      name={id}
      label={label}
      type="date"
      defaultValue={value}
      InputLabelProps={{
        shrink: true
      }}
      fullWidth
      onChange={handleChange}
      className={classes.datePicker}
      InputProps={{
        inputProps: {
          ...inputProps,
          format: "yyyy-MM-dd"
        }
      }}
      error={!!localError[id]}
      FormHelperTextProps={{
        className: classes.helperText
      }}
      helperText={maybe(() => localError[id], null)}
      disabled={disabled}
    />
  );
};

const TimePicker = ({
  id,
  label,
  value,
  onChange,
  inputProps,
  disabled
}: DateTimePickerProps) => {
  const { localError } = useCouponScheduling();
  const classes = useStyles({});

  const [timeValue, setTimeValue] = React.useState(value || "00:00");

  const handleChange = e => {
    setTimeValue(e.target.value);
    onChange({
      target: {
        name: e.target.name,
        value: e.target.value
      }
    });
  };

  return (
    <TextField
      id={id}
      name={id}
      label={label}
      type="time"
      value={timeValue}
      defaultValue={value}
      InputLabelProps={{
        shrink: true
      }}
      onChange={handleChange}
      fullWidth
      InputProps={{
        inputProps: {
          ...inputProps
        }
      }}
      FormHelperTextProps={{
        className: classes.helperText
      }}
      error={!!localError[id]}
      helperText={maybe(() => localError[id], "")}
      disabled={disabled}
    />
  );
};

const OccurrenceSelection = () => {
  const { activeTab, data, onChange, disabled } = useCouponScheduling();

  if (activeTab === CouponsTab.Event) {
    return null;
  }

  const classes = useStyles({});
  const choices = Object.values(redeemingOptions).map(redeemingOption => ({
    label: redeemingOption.label,
    value: redeemingOption.value
  }));

  const isDisabled = activeTab === CouponsTab.Redeeming;

  return (
    <SingleSelectField
      choices={choices}
      className={classes.redeemingSelection}
      onChange={onChange}
      label={
        activeTab === CouponsTab.Coupon ? "Occurrence" : "Redemption Frequency"
      }
      name="occurrence"
      value={data.occurrence}
      disabled={isDisabled || disabled}
    />
  );
};

const ExcludeDays = () => {
  const {
    activeTab,
    data,
    onChange,
    localError,
    disabled
  } = useCouponScheduling();

  if (activeTab === CouponsTab.Event) {
    return null;
  }

  const intl = useIntl();
  const classes = useStyles({});
  const { excludedDays } = data;
  const handleExcludeDay = (day: string) => {
    if (excludedDays.includes(day)) {
      onChange({
        target: {
          name: "excludedDays",
          value: excludedDays.filter(excludedDay => excludedDay !== day)
        }
      });
    } else {
      onChange({
        target: {
          name: "excludedDays",
          value: excludedDays.concat(day)
        }
      });
    }
  };
  return (
    <Box>
      <Typography component="h4" style={{ color: "#747C8C", marginBottom: 4 }}>
        {intl.formatMessage({
          defaultMessage: "Exclude:"
        })}
      </Typography>
      <Box className={classes.chips}>
        {dayOptions.map(({ label, value }) => (
          <Chip
            id={`${label.toLowerCase()}Chip`}
            label={label}
            key={value}
            disabled={activeTab === CouponsTab.Redeeming || disabled}
            className={classNames(classes.chipItem, {
              [classes.activeChipItem]: excludedDays.includes(value)
            })}
            onClick={() => handleExcludeDay(value)}
          />
        ))}
      </Box>
      {localError.excludedDays && activeTab === CouponsTab.Coupon ? (
        <FormHelperText className={classes.helperText} error={true}>
          {localError.excludedDays}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default CouponScheduling;
